import React from "react";
import { useLocation } from "react-router-dom";
import { useHashInfo } from "../Hooks/UseHashInfo";
import { ContextualMenu, Icon, IconButton, IContextualMenuItem } from "@fluentui/react";

export interface TabOptions {
    id: string;
    title: string;
    content: React.ReactNode;
    linkOptions?: TabLinkOptions;
    hidden?: boolean;
}

interface TabLinkOptions {
    link: string;
    openInNewTab?: boolean;
}

interface Props {
    tabs: TabOptions[];
}

interface TabLink {
    item: TabOptions;
    isLinkTab: boolean;
    opensInNewTab: boolean;
    href: string;
}

const MobileTabs = React.memo((props: { tabLinks: TabLink[]; currentTab: string }) => {
    const { tabLinks, currentTab } = props;
    const containerRef = React.useRef<HTMLDivElement>(null);
    const measureRef = React.useRef<HTMLDivElement>(null);
    const [visibleCount, setVisibleCount] = React.useState(0);
    const [isOverflowing, setIsOverflowing] = React.useState(false);
    const [pinSelectedTab, setPinSelectedTab] = React.useState(false);
    const [menuTarget, setMenuTarget] = React.useState<HTMLElement>();
    const selectedTab = tabLinks.find(({ item }) => item.id === currentTab);
    const unpinnedTabs = tabLinks.filter(({ item }) => item.id !== currentTab);

    React.useLayoutEffect(() => {
        const container = containerRef.current;
        const measure = measureRef.current;
        if (!container || !measure) {
            return;
        }

        const updateVisibleCount = () => {
            const itemWidths = Array.from(measure.children).map((element) => element.getBoundingClientRect().width);
            const totalWidth = itemWidths.reduce((total, width) => total + width, 0);
            if (totalWidth <= container.clientWidth) {
                setIsOverflowing(false);
                setPinSelectedTab(false);
                setVisibleCount(tabLinks.length);
                return;
            }

            const selectedIndex = tabLinks.findIndex(({ item }) => item.id === currentTab);
            const selectedWidth = selectedIndex >= 0 ? itemWidths[selectedIndex] : 0;
            const widthById = new Map(tabLinks.map(({ item }, index) => [item.id, itemWidths[index]]));
            const countFittingTabs = (links: TabLink[], availableWidth: number) => {
                let usedWidth = 0;
                let count = 0;
                for (const { item } of links) {
                    const itemWidth = widthById.get(item.id) ?? 0;
                    if (usedWidth + itemWidth > availableWidth) {
                        break;
                    }
                    usedWidth += itemWidth;
                    count++;
                }
                return count;
            };

            const primaryVisibleCount = countFittingTabs(tabLinks, container.clientWidth - 40);
            const selectedTabFits = selectedIndex >= 0 && selectedIndex < primaryVisibleCount;
            if (selectedTabFits) {
                setPinSelectedTab(false);
                setVisibleCount(primaryVisibleCount);
            } else {
                setPinSelectedTab(selectedIndex >= 0);
                setVisibleCount(countFittingTabs(unpinnedTabs, container.clientWidth - selectedWidth - 40));
            }
            setIsOverflowing(true);
        };

        updateVisibleCount();
        const resizeObserver = new ResizeObserver(updateVisibleCount);
        resizeObserver.observe(container);
        return () => resizeObserver.disconnect();
    }, [currentTab, tabLinks]);

    const renderTab = (tabLink: TabLink) => {
        const { item, isLinkTab, opensInNewTab, href } = tabLink;
        const hrefFields = isLinkTab
            ? {
                target: opensInNewTab ? "_blank" : "_self",
                rel: "noopener noreferrer",
            }
            : {};
        const className = item.id === currentTab
            ? "fxs-header-tab fxs-header-tab-selected"
            : "fxs-header-tab";

        return (
            <div className={className} key={item.id}>
                <a href={href} {...hrefFields}>
                    {item.title}{" "}
                    {isLinkTab && opensInNewTab && <Icon iconName="OpenInNewWindow" />}
                </a>
            </div>
        );
    };

    const visibleTabs = !isOverflowing
        ? tabLinks
        : pinSelectedTab
            ? unpinnedTabs.slice(0, visibleCount)
            : tabLinks.slice(0, visibleCount);
    const overflowTabs = !isOverflowing
        ? []
        : pinSelectedTab
            ? unpinnedTabs.slice(visibleCount)
            : tabLinks.slice(visibleCount);
    const menuItems: IContextualMenuItem[] = overflowTabs.map(({ item, isLinkTab, opensInNewTab, href }) => ({
        key: item.id,
        text: item.title,
        href,
        target: opensInNewTab ? "_blank" : "_self",
        rel: isLinkTab ? "noopener noreferrer" : undefined,
        className: "fxs-mobile-tabs-menu-item",
        onRenderContent: (props) => <span className="fxs-mobile-tabs-menu-content">
            <span>{props.item.text}</span>
            {opensInNewTab && <Icon className="fxs-mobile-tabs-menu-external-icon" iconName="OpenInNewWindow" />}
        </span>,
    }));

    return (
        <div className="fxs-mobile-tabs" ref={containerRef}>
            <div className="fxs-mobile-tabs-items">
                {visibleTabs.map(renderTab)}
            </div>
            {overflowTabs.length > 0 && <>
                {pinSelectedTab && selectedTab && <div className="fxs-mobile-tabs-current">
                    {renderTab(selectedTab)}
                </div>}
                <IconButton
                    className="fxs-mobile-tabs-overflow"
                    ariaLabel="More tabs"
                    iconProps={{ iconName: menuTarget ? "ChevronUp" : "ChevronDown" }}
                    onClick={(event) => setMenuTarget(menuTarget ? undefined : event.currentTarget as HTMLElement)}
                />
                {menuTarget && <ContextualMenu
                    target={menuTarget}
                    items={menuItems}
                    isBeakVisible={false}
                    gapSpace={0}
                    onDismiss={() => setMenuTarget(undefined)}
                />}
            </>}
            <div aria-hidden className="fxs-mobile-tabs-measure" ref={measureRef}>
                {tabLinks.map(renderTab)}
            </div>
        </div>
    );
});

const Tabs = (props: Props) => {
    const tabs = props.tabs.filter((item) => item.hidden !== true);
    const hashInfo = useHashInfo();
    const currentTab = hashInfo.path || tabs[0]?.id;
    const location = useLocation();

    const tabLinks = tabs.map((item) => {
        const linkOptions = item.linkOptions;
        const isLinkTab = linkOptions?.link !== undefined;
        const opensInNewTab = linkOptions?.openInNewTab === true;
        const href = isLinkTab && linkOptions
            ? linkOptions.link
            : `${location.pathname}${window.location.search}#${item.id}`;

        return { item, isLinkTab, opensInNewTab, href };
    });

    return (
        <>
            <div className="fxs-header fxs-desktop-tabs">
                {tabLinks.map(({ item, isLinkTab, opensInNewTab, href }) => {
                    const hrefFields = isLinkTab
                        ? {
                            target: opensInNewTab ? "_blank" : "_self",
                            rel: "noopener noreferrer",
                        }
                        : {};

                    let className = "fxs-header-tab";
                    if (item.id === currentTab) {
                        className += " fxs-header-tab-selected";
                    }

                    return (
                        <div className={className} key={item.id}>
                            <a href={href} {...hrefFields}>
                                {item.title}{" "}
                                {isLinkTab && opensInNewTab && (
                                    <Icon iconName="OpenInNewWindow" />
                                )}
                            </a>
                        </div>
                    );
                })}
            </div>
            <MobileTabs tabLinks={tabLinks} currentTab={currentTab} />
            <div className="fxs-content">
                {tabs.find((item) => item.id === currentTab)?.content}
            </div>
        </>
    );
};

export default React.memo(Tabs);
